import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  startAfter,
  doc,
  updateDoc,
} from "firebase/firestore";
import { db } from "../firebase/firebaseConfig";
import { formatMinutes } from "./timeEntry";

/**
 * ArchiveJobs
 * - Two tabs: Breakdowns (from /machines) and Work Requests (from /work_requests)
 * - Shows only archived items (status === "complete")
 * - Search, date range, sort by completion date, pagination
 * - Unarchive action: sets status -> "inprogress" and clears dateOfCompletion
 */

const PAGE_SIZE = 50;

export default function ArchiveJobs() {
  const [tab, setTab] = useState("breakdowns"); // "breakdowns" | "work_requests"

  // shared UI state
  const [search, setSearch] = useState("");
  const [fromDate, setFromDate] = useState(""); // yyyy-mm-dd
  const [toDate, setToDate] = useState("");     // yyyy-mm-dd
  const [sortDir, setSortDir] = useState("desc"); // "asc" | "desc"
  const [loading, setLoading] = useState(false);

  // data state per tab
  const [rows, setRows] = useState([]);
  const [lastDocSnap, setLastDocSnap] = useState(null);
  const [hasMore, setHasMore] = useState(false);

  // fetch
  useEffect(() => {
    // reset when switching tab / filters / sort
    setRows([]);
    setLastDocSnap(null);
    fetchPage("reset");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, fromDate, toDate, sortDir]);

  async function fetchPage(mode = "append") {
    setLoading(true);
    try {
      const colName = tab === "breakdowns" ? "machines" : "work_requests";
      const colRef = collection(db, colName);

      // Try ideal query (status complete + order by dateOfCompletion + optional cursor)
      let q = query(
        colRef,
        where("status", "==", "complete"),
        orderBy("dateOfCompletion", sortDir === "asc" ? "asc" : "desc"),
        limit(PAGE_SIZE)
      );

      if (lastDocSnap && mode !== "reset") {
        q = query(
          colRef,
          where("status", "==", "complete"),
          orderBy("dateOfCompletion", sortDir === "asc" ? "asc" : "desc"),
          startAfter(lastDocSnap),
          limit(PAGE_SIZE)
        );
      }

      let snap;
      try {
        snap = await getDocs(q);
      } catch (err) {
        // Fall back if index not ready: order by timestamp instead (works but not perfect sort).
        if (String(err?.message || "").includes("requires an index")) {
          const fallback = lastDocSnap && mode !== "reset"
            ? query(
                colRef,
                where("status", "==", "complete"),
                orderBy("timestamp", sortDir === "asc" ? "asc" : "desc"),
                startAfter(lastDocSnap),
                limit(PAGE_SIZE)
              )
            : query(
                colRef,
                where("status", "==", "complete"),
                orderBy("timestamp", sortDir === "asc" ? "asc" : "desc"),
                limit(PAGE_SIZE)
              );
          snap = await getDocs(fallback);
          console.warn(
            "[ArchiveJobs] Composite index missing for dateOfCompletion. Using fallback on timestamp until index is created in Firestore."
          );
        } else {
          throw err;
        }
      }

      const docs = snap.docs.map((d) => ({ id: d.id, ...d.data(), _snap: d }));
      // Apply date-range and search client-side (cheap on PAGE_SIZE)
      const filtered = docs.filter((row) => {
        const done = toJsDate(row.dateOfCompletion) || toJsDate(row.timestamp);
        if (fromDate && done && done < new Date(fromDate + "T00:00:00")) return false;
        if (toDate && done && done > new Date(toDate + "T23:59:59")) return false;

        const hay = [
          row.machine,
          row.fault,
          row.faultType,
          row.name,
          row.notes,
          row.engineerNotes,
          row.description,
          row.location,
          row.priority,
          row.requestType,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();

        return hay.includes(search.toLowerCase());
      });

      setRows((cur) => (mode === "reset" ? filtered : [...cur, ...filtered]));
      setLastDocSnap(snap.docs[snap.docs.length - 1] || null);
      setHasMore(snap.docs.length === PAGE_SIZE);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }

  // cheap search (client-side) to avoid more Firestore indexes
  const visible = useMemo(() => {
    if (!search.trim()) return rows;
    return rows.filter((r) => {
      const hay = [
        r.machine,
        r.fault,
        r.faultType,
        r.name,
        r.notes,
        r.engineerNotes,
        r.description,
        r.location,
        r.priority,
        r.requestType,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(search.toLowerCase());
    });
  }, [rows, search]);

  const unarchive = async (row) => {
    const colName = tab === "breakdowns" ? "machines" : "work_requests";
    const ref = doc(db, colName, row.id);
    await updateDoc(ref, {
      status: "inprogress",
      dateOfCompletion: null,
    });
    // remove from view
    setRows((cur) => cur.filter((r) => r.id !== row.id));
  };

const exportCsv = async () => {
  if (!fromDate || !toDate) {
    alert("Select a date range (e.g. a full month) before exporting.");
    return;
  }

  const colName = tab === "breakdowns" ? "machines" : "work_requests";
  const colRef = collection(db, colName);

  try {
    const q = query(
      colRef,
      where("status", "==", "complete"),
      where("dateOfCompletion", ">=", new Date(fromDate + "T00:00:00")),
      where("dateOfCompletion", "<=", new Date(toDate + "T23:59:59")),
      orderBy("dateOfCompletion", "desc")
    );

    const snap = await getDocs(q);

    const data = snap.docs.map((d) => d.data());

    const header =
      tab === "breakdowns"
        ? [
            "Machine",
            "Fault",
            "Type",
            "Reported By",
            "Completed",
            "Time Taken",
            "Ack By",
            "Notes",
            "Engineer Notes",
          ]
        : [
          "Request",
          "Priority",
          "Location",
          "Requested By",
          "Completed",
          "Time Taken",
          "Ack By",
          "Notes",
          "Engineer Notes",
        ];

    const lines = [
      header.join(","),
      ...data.map((r) => {
        if (tab === "breakdowns") {
          return csv([
            r.machine,
            r.fault,
            r.faultType,
            r.name,
            fmtDateTime(r.dateOfCompletion || r.timestamp),
            formatTimeTakenRow(r),
            r.acknowledgedBy || "",
            r.notes || "",
            r.engineerNotes || "",
          ]);
        } else {
          return csv([
            r.requestType || r.description || "(Request)",
            r.priority || "",
            r.location || "",
            r.name || "",
            fmtDateTime(r.dateOfCompletion || r.timestamp),
            formatTimeTakenRow(r),
            r.acknowledgedBy || "",
            r.notes || "",
            r.engineerNotes || "",
          ]);
        }
      }),
    ].join("\n");

    const blob = new Blob([lines], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");

    const file =
      tab === "breakdowns"
        ? `breakdowns_${fromDate}_to_${toDate}.csv`
        : `work_requests_${fromDate}_to_${toDate}.csv`;

    a.href = url;
    a.setAttribute("download", file);
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  } catch (err) {
    console.error("CSV export failed:", err);
    alert("Export failed — likely missing Firestore index.");
  }
};

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="max-w-7xl mx-auto px-6 pt-6">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Archive</h1>
            <p className="text-gray-500 text-sm">
              Completed work — split between Breakdowns and Work Requests. Unarchive to reopen.
            </p>
          </div>

          <div className="flex items-center gap-2">
            <Tab isActive={tab === "breakdowns"} onClick={() => setTab("breakdowns")}>
              Breakdowns
            </Tab>
            <Tab isActive={tab === "work_requests"} onClick={() => setTab("work_requests")}>
              Work Requests
            </Tab>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="max-w-7xl mx-auto px-6 mt-5">
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-4">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-3">
            <div className="lg:col-span-2">
              <label className="block text-xs font-semibold text-gray-600 mb-1">Search</label>
              <input
                className="w-full p-2 border rounded-md"
                placeholder={
                  tab === "breakdowns"
                    ? "Machine, fault, notes, engineer…"
                    : "Request, location, notes, engineer…"
                }
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Completed From</label>
              <input
                type="date"
                className="w-full p-2 border rounded-md"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Completed To</label>
              <input
                type="date"
                className="w-full p-2 border rounded-md"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
              />
            </div>

            <div className="flex gap-2 items-end">
              <div className="flex-1">
                <label className="block text-xs font-semibold text-gray-600 mb-1">Sort</label>
                <select
                  className="w-full p-2 border rounded-md"
                  value={sortDir}
                  onChange={(e) => setSortDir(e.target.value)}
                >
                  <option value="desc">Newest first</option>
                  <option value="asc">Oldest first</option>
                </select>
              </div>
              <button
                className="px-3 py-2 rounded-md border bg-white hover:bg-gray-50"
                onClick={() => fetchPage("reset")}
                disabled={loading}
                title="Refresh now"
              >
                ⟳
              </button>
              <button className="px-3 py-2 rounded-md border bg-white hover:bg-gray-50" onClick={exportCsv}>
                Export CSV
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="max-w-7xl mx-auto px-6 mt-5 pb-12">
        <div className="rounded-xl border border-gray-200 overflow-hidden bg-white shadow-sm">
          <div className="sticky top-0 z-10 bg-gray-50 border-b border-gray-200">
            {tab === "breakdowns" ? (
              <HeaderRow
                cols={[
                "Machine",
                "Fault",
                "Type",
                "Reported By",
                "Completed",
                "Time Taken",
                "Ack By",
                "Notes",
                "Engineer Notes",
                "",
              ]}
              />
            ) : (
              <HeaderRow
                cols={[
                  "Request",
                  "Priority",
                  "Location",
                  "Requested By",
                  "Completed",
                  "Time Taken",
                  "Ack By",
                  "Notes",
                  "Engineer Notes",
                  "",
                ]}
              />
            )}
          </div>

          {visible.length === 0 ? (
            <div className="p-6 text-sm text-gray-500">No archived items found.</div>
          ) : (
            <ul className="divide-y divide-gray-100">
              {visible.map((row) =>
                tab === "breakdowns" ? (
                  <li key={row.id} className="hover:bg-gray-50">
                    <Row>
                      <Cell primary>{row.machine || "—"}</Cell>
                      <Cell>{row.fault || "—"}</Cell>
                      <Cell>{row.faultType || "—"}</Cell>
                      <Cell>{row.name || "—"}</Cell>
                      <Cell mono>{fmtDateTime(row.dateOfCompletion || row.timestamp)}</Cell>
                      <Cell>
                        {formatTimeTakenRow(row)}
                      </Cell>
                      <Cell>{row.acknowledgedBy || "—"}</Cell>
                      <Cell className="max-w-[22rem]" title={timeAwareNotesTitle(row)}>
                        <TimeAwareNotes row={row} />
                      </Cell>
                      <Cell className="max-w-[22rem] truncate" title={row.engineerNotes || ""}>
                        {row.engineerNotes || "—"}
                      </Cell>
                      <Cell action>
                        <button className="btn-secondary" onClick={() => unarchive(row)}>
                          Unarchive
                        </button>
                      </Cell>
                    </Row>
                  </li>
                ) : (
                  <li key={row.id} className="hover:bg-gray-50">
                    <Row>
                      <Cell primary>{row.requestType || row.description || "—"}</Cell>
                      <Cell>
                        <Badge tone={badgeTone(row.priority)}>{row.priority || "Normal"}</Badge>
                      </Cell>
                      <Cell>{row.location || "—"}</Cell>
                      <Cell>{row.name || "—"}</Cell>
                      <Cell mono>{fmtDateTime(row.dateOfCompletion || row.timestamp)}</Cell>
                      <Cell>
                        {formatTimeTakenRow(row)}
                      </Cell>
                      <Cell>{row.acknowledgedBy || "—"}</Cell>
                      <Cell className="max-w-[22rem]" title={timeAwareNotesTitle(row)}>
                        <TimeAwareNotes row={row} />
                      </Cell>
                      <Cell className="max-w-[22rem] truncate" title={row.engineerNotes || ""}>
                        {row.engineerNotes || "—"}
                      </Cell>
                      <Cell action>
                        <div className="flex items-center gap-2">
                          {row?.sharepoint?.folderWebUrl ? (
                            <a
                              href={row.sharepoint.folderWebUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="btn-ghost"
                              title="Open SharePoint folder"
                            >
                              Folder
                            </a>
                          ) : null}
                          <button className="btn-secondary" onClick={() => unarchive(row)}>
                            Unarchive
                          </button>
                        </div>
                      </Cell>
                    </Row>
                  </li>
                )
              )}
            </ul>
          )}

          {/* Footer / pagination */}
          <div className="border-t border-gray-200 p-3 flex items-center justify-between">
            <div className="text-xs text-gray-500">
              Showing <strong>{visible.length}</strong> items
              {rows.length !== visible.length ? ` (filtered)` : ""}.
            </div>
            <div className="flex items-center gap-2">
              <button
                className="px-3 py-1.5 rounded-md border bg-white hover:bg-gray-50"
                onClick={() => fetchPage("append")}
                disabled={!hasMore || loading}
              >
                {loading ? "Loading…" : hasMore ? "Load more" : "No more"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Styles for buttons/badges to keep it sleek */}
      <style>{`
        .btn-secondary {
          @apply text-sm px-3 py-1.5 rounded-md border border-gray-300 bg-white hover:bg-gray-50;
        }
        .btn-ghost {
          @apply text-sm px-3 py-1.5 rounded-md border border-gray-200 bg-white hover:bg-gray-50 text-gray-700;
        }
      `}</style>
    </div>
  );
}

/* ─────────── atoms ─────────── */

function Tab({ isActive, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className={`text-sm px-3 py-1.5 rounded-md border ${
        isActive ? "bg-gray-900 text-white border-gray-900" : "bg-white hover:bg-gray-50"
      }`}
    >
      {children}
    </button>
  );
}

function HeaderRow({ cols }) {
  return (
    <div className="grid grid-cols-[1.2fr,1fr,1fr,1fr,1fr,.8fr,1fr,1.2fr,1.2fr,120px] text-xs font-semibold text-gray-600 px-4 py-3">
      {cols.map((c, i) => (
        <div key={i} className={`${i === 0 ? "truncate pr-4" : "truncate"}`}>
          {c}
        </div>
      ))}
    </div>
  );
}

function Row({ children }) {
  return (
    <div className="grid grid-cols-[1.2fr,1fr,1fr,1fr,1fr,.8fr,1fr,1.2fr,1.2fr,120px] px-4 py-3">
      {children}
    </div>
  );
}

function Cell({ children, primary = false, mono = false, action = false, className = "", ...rest }) {
  return (
    <div
      className={`${primary ? "font-medium text-gray-900 pr-4" : "text-gray-800"} ${
        mono ? "font-mono text-[12.5px]" : "text-sm"
      } ${action ? "flex items-center justify-end" : ""} ${className}`}
      {...rest}
    >
      {children}
    </div>
  );
}

function Badge({ children, tone = "neutral" }) {
  const cls = {
    red: "bg-red-50 text-red-700 border-red-200",
    amber: "bg-amber-50 text-amber-700 border-amber-200",
    green: "bg-emerald-50 text-emerald-700 border-emerald-200",
    sky: "bg-sky-50 text-sky-700 border-sky-200",
    neutral: "bg-gray-50 text-gray-700 border-gray-200",
  }[tone];
  return <span className={`text-xs border rounded-full px-2 py-0.5 ${cls}`}>{children}</span>;
}

function badgeTone(priority) {
  if (!priority) return "neutral";
  const p = String(priority).toLowerCase();
  if (p === "urgent") return "red";
  if (p === "high") return "amber";
  if (p === "low") return "sky";
  return "green";
}

/* ─────────── utils ─────────── */

function toJsDate(v) {
  if (!v) return null;
  if (v instanceof Date) return v;
  if (v?.toDate) return v.toDate();
  if (typeof v === "string" && /^\d{4}-\d{2}-\d{2}$/.test(v)) return new Date(v + "T00:00:00");
  return null;
}

function fmtDateTime(v) {
  const d = toJsDate(v);
  return d ? d.toLocaleString() : "—";
}

function formatTimeTakenRow(row) {
  return row.timeTakenLabel || formatMinutes(row.timeTakenMinutes);
}

function timeAwareNotesTitle(row) {
  return [
    row.notes,
    row.timeTakenExceededLimit ? "Time exceeded: >= 99 days (approximate)" : "",
    row.timeTakenNote ? `Time note: ${row.timeTakenNote}` : "",
  ]
    .filter(Boolean)
    .join("\n");
}

function TimeAwareNotes({ row }) {
  const hasTimeDetails = row.timeTakenExceededLimit || row.timeTakenNote;

  return (
    <div className="space-y-1">
      <div className="truncate">{row.notes || (hasTimeDetails ? "" : "—")}</div>
      {row.timeTakenExceededLimit ? (
        <div className="text-xs font-semibold text-amber-700">
          ≥ 99 days (approximate)
        </div>
      ) : null}
      {row.timeTakenNote ? (
        <div className="truncate text-xs text-amber-800">
          Time note: {row.timeTakenNote}
        </div>
      ) : null}
    </div>
  );
}

function csv(arr) {
  return arr
    .map((x) => {
      const s = (x ?? "").toString().replace(/"/g, '""');
      return /[",\n]/.test(s) ? `"${s}"` : s;
    })
    .join(",");
}
